import React from 'react';
import {
  VictoryContainer,
  VictoryLabel,
  VictoryPie,
  VictoryTooltip,
  VictoryTooltipProps,
} from 'victory';
import Legend from 'components/Charts/Legend';
import CustomSlice from 'components/Charts/CustomSlice';
import { useChart } from 'hooks/useChart';
import { useCallback } from 'react';
import debounce from 'lodash/debounce';

export interface DonutChartData {
  x: string;
  y: number;
  backgroundColor: string;
}

interface CustomTooltipProps extends VictoryTooltipProps {
  isActive?: (datum: DonutChartData) => boolean;
}

const CustomTooltip = (props: CustomTooltipProps) => {
  const isActive =
    props.isActive &&
    props.datum &&
    props.isActive(props.datum as DonutChartData);
  return <VictoryTooltip {...props} active={isActive || props.active} />;
};

interface DonutChartProps {
  data: DonutChartData[];
  scrollLegendOnPieHover?: boolean;
  total: number;
  onClick?: (datum: DonutChartData) => void;
}

const CustomDonutChart: React.FC<DonutChartProps> = ({
  data,
  scrollLegendOnPieHover = false,
  total,
  onClick,
}) => {
  const {
    dataSet,
    scrollToLegendItem,
    legendContainerRef,
    legendItemRefs,
    hoveredDatum,
    setHoveredDatum,
  } = useChart(data);
  const debouncedClearHoveredDatum = debounce(() => {
    setHoveredDatum(undefined);
  }, 150);

  const handleMouseOver = useCallback(
    (datum: DonutChartData) => {
      debouncedClearHoveredDatum.cancel();
      setHoveredDatum(datum);
    },
    [setHoveredDatum, debouncedClearHoveredDatum],
  );

  const handleMouseOut = useCallback(() => {
    debouncedClearHoveredDatum();
  }, [debouncedClearHoveredDatum]);

  return (
    <div>
      <div className="flex w-full flex-col items-center justify-center gap-8">
        <div className="">
          <svg
            width="273"
            height="137"
            viewBox="0 0 273 137"
            role="img"
            className="h-[137px] w-[273px] overflow-visible"
            style={{ pointerEvents: 'all' }}
          >
            <VictoryPie
              standalone={false}
              padding={0}
              data={dataSet.reverse()}
              innerRadius={105}
              width={273}
              height={273}
              radius={130}
              padAngle={1.5}
              dataComponent={
                <CustomSlice
                  isHighlighted={({ x }: DonutChartData) =>
                    x === hoveredDatum?.x
                  }
                />
              }
              startAngle={90}
              endAngle={-90}
              events={[
                {
                  target: 'data',
                  eventHandlers: {
                    onMouseOver: (_, dataComponentProps) => {
                      const datum = dataComponentProps.datum as DonutChartData;
                      scrollLegendOnPieHover && scrollToLegendItem(datum.x);
                      return [
                        {
                          target: 'data',
                          mutation: () => handleMouseOver(datum),
                        },
                      ];
                    },
                    onMouseOut: () => {
                      return [
                        {
                          target: 'data',
                          mutation: () => handleMouseOut(),
                        },
                      ];
                    },
                    onClick: (_, dataComponentProps) => {
                      const datum = dataComponentProps.datum as DonutChartData;
                      scrollLegendOnPieHover && scrollToLegendItem(datum.x);
                      return [
                        {
                          target: 'data',
                          mutation: () => {
                            if (onClick) {
                              handleMouseOver(datum);
                              onClick(datum);
                            }
                          },
                        },
                      ];
                    },
                  },
                },
              ]}
              labels={({ datum }) => `${datum.x}`}
              labelComponent={
                <CustomTooltip
                  isActive={({ x }) => x === hoveredDatum?.x}
                  cornerRadius={8}
                  pointerWidth={24}
                  pointerLength={10}
                  orientation="top"
                  style={{
                    fontFamily: 'Poppins',
                    fill: 'rgb(var(--content-base-subdued))',
                    fontWeight: '400',
                    fontSize: '14px',
                  }}
                  flyoutPadding={20}
                  flyoutStyle={{
                    fill: 'rgb(var(--_background-base-surface-0))',
                    maxWidth: '120px',
                    borderRadius: '8px',
                    border: '1px solid transparent',
                    boxShadow:
                      '0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
                  }}
                />
              }
              cornerRadius={4}
              style={{
                data: {
                  fill: ({ datum }) => datum.backgroundColor,
                },
              }}
              containerComponent={
                <VictoryContainer className="[&>svg]:overflow-visible " />
              }
            />
            <VictoryLabel
              textAnchor="middle"
              style={{
                fontFamily: 'Poppins',
                fontSize: 65,
                fontWeight: 600,
                fill: 'rgb(var(--content-base-default))',
                lineHeight: '80%',
              }}
              x={140}
              y={100}
              text={hoveredDatum?.y ?? total}
            />
          </svg>
        </div>
        <div className="relative max-h-[160px] w-full">
          <div
            className="grid w-full grid-cols-2 flex-col items-start justify-start gap-x-2"
            ref={legendContainerRef}
          >
            {data.map((datum) => (
              <Legend
                className="cursor-pointer"
                key={datum.x}
                ref={(el) => (legendItemRefs.current[datum.x] = el)}
                backgroundColor={datum.backgroundColor}
                x={datum.x}
                y={datum.y}
                onMouseOver={() => handleMouseOver(datum)}
                onMouseOut={handleMouseOut}
                onFocus={() => handleMouseOver(datum)}
                onBlur={handleMouseOut}
                onClick={() => onClick?.(datum)}
                active={hoveredDatum?.x === datum.x}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDonutChart;
